import { Text } from '@user-portal/wikr-components';
import { useTranslation } from 'react-i18next';
import { useMemo, ReactElement, useRef, useEffect } from 'react';

import { appAccessCodeSelector, isAppAccessCodeFailedSelector } from 'store/auth/selectors';
import { getAppAccessCodeRequest } from 'store/auth/actions';

import { sendAnalyticAccessCodeLoaded } from 'services/Analytics/trackers/accessCode';

import { useDispatch, useSelector } from 'hooks/store';

import Step from './components/Step/Step';
import LazyVideo from './components/LazyVideo/LazyVideo';
import DownloadWithQrCode from './components/DownloadWithQrCode';
import DownloadWithMobileLinks from './components/DownloadWithMobileLinks';
import CopyCodeStep from './components/CopyCodeStep';

import * as S from './styled';

interface UseGetStepsParams {
    isAppDownloaded: boolean | null;
    isIos: boolean;
    isAndroid: boolean;
    deepLinkUrl: string;
    handleDownloadApp: (eventLabel: string, isDeeplinkBtn?: boolean) => void;
}

export const useGetSteps = ({
    isAppDownloaded,
    isIos,
    isAndroid,
    deepLinkUrl,
    handleDownloadApp,
}: UseGetStepsParams): Array<(stepNumber: number) => ReactElement> => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const isMobileDevice = isAndroid || isIos;

    const appAccessCodeData = useSelector(appAccessCodeSelector);
    const { access_code, expires_at } = appAccessCodeData ?? {};
    const isAppAccessCodeFailed = useSelector(isAppAccessCodeFailedSelector);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const fifthStepTitle =
        isMobileDevice && access_code
            ? 'main.step.enterCopiedCode.mobile.title'
            : 'main.step.enterCopiedCode.desktop.title';

    useEffect(() => {
        if (access_code) {
            sendAnalyticAccessCodeLoaded(isMobileDevice);
        } else {
            dispatch(getAppAccessCodeRequest());
        }
    }, [dispatch, access_code, isMobileDevice]);

    useEffect(() => {
        if (expires_at && access_code) {
            const expiresAtTimestamp = new Date(expires_at).getTime();
            const currentTimestamp = Date.now();

            const timeout = expiresAtTimestamp - currentTimestamp - 60000;

            if (timeout > 0) {
                timeoutRef.current = setTimeout(() => {
                    dispatch(getAppAccessCodeRequest());
                }, timeout);
            } else {
                dispatch(getAppAccessCodeRequest());
            }
        }

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, [expires_at, access_code, dispatch]);

    return useMemo(
        () => [
            (stepNumber: number) => (
                <S.ThirdStep key={stepNumber} withoutAccessCode={isAppAccessCodeFailed}>
                    <Step stepNumber={stepNumber} dataLocator="deeplinkContainer">
                        <>
                            {isAppDownloaded ? (
                                <Text type="large-text" bold mb={16} center>
                                    {t('main.step.title.getApp')}
                                </Text>
                            ) : (
                                <>
                                    <S.GetAppTextMobile type="large-text" bold mb={16} center>
                                        {t('main.step.title.getAppYoga')}
                                    </S.GetAppTextMobile>
                                    <S.GetAppTextDesktop type="large-text" bold mb={16} center>
                                        {t('main.accessCodeFeature.step.title.getAppYogaDesktop')}
                                    </S.GetAppTextDesktop>
                                </>
                            )}

                            {isMobileDevice ? (
                                <DownloadWithMobileLinks
                                    isIos={isIos}
                                    isAndroid={isAndroid}
                                    handleLinkDownloadApp={handleDownloadApp}
                                />
                            ) : (
                                <DownloadWithQrCode deepLinkUrl={deepLinkUrl} />
                            )}
                        </>
                    </Step>
                </S.ThirdStep>
            ),
            ...(!isAppAccessCodeFailed
                ? [
                      (stepNumber: number) => (
                          <S.FourthStep key={stepNumber}>
                              <Step stepNumber={stepNumber} dataLocator="accessCodeContainer">
                                  <CopyCodeStep isMobileDevice={Boolean(isMobileDevice)} access_code={access_code} />
                              </Step>
                          </S.FourthStep>
                      ),
                  ]
                : []),
            (stepNumber: number) => (
                <S.FifthStep key={stepNumber}>
                    <Step stepNumber={stepNumber} dataLocator="videoContainer">
                        <>
                            <Text type="large-text" bold mb={16} center>
                                {t(fifthStepTitle)}
                            </Text>
                            <LazyVideo dataLocator="videoElement" />
                        </>
                    </Step>
                </S.FifthStep>
            ),
        ],
        [
            isAppDownloaded,
            isMobileDevice,
            isIos,
            isAndroid,
            deepLinkUrl,
            handleDownloadApp,
            access_code,
            fifthStepTitle,
            isAppAccessCodeFailed,
            t,
        ]
    );
};
